import { useCallback, useEffect, useRef, useState } from 'react';
import { GameFrame, PromptLine, pick, shuffle, useGame, useRoundTimer, type GameProps } from '../sdk/react';
import { MOVERS, placePrompt } from '../phrases';
import { Chip, DropSpot, usePlacer } from '../kit/place';
import { SceneView } from '../scenes/SceneView';
import { sceneOf, type ScenePart } from '../scenes/scenes';
import { KINDS } from '../../concepts/kinds';

export default function PlaceIt(props: GameProps) {
  const game = useGame(props);
  const scene = sceneOf(game.config?.scene);
  const wanted = Array.isArray(game.config?.parts) ? (game.config!.parts as string[]) : [];
  const zones = scene ? scene.parts.filter((p) => wanted.includes(p.id)) : [];
  const mover = MOVERS[game.str('mover', 'cat')] ?? MOVERS.cat;
  const [target, setTarget] = useState<ScenePart | null>(null);
  const [trial, setTrial] = useState<string | null>(null);
  const [placedAt, setPlacedAt] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const queue = useRef<ScenePart[]>([]);
  const busy = useRef(false);

  const start = useCallback(async () => {
    if (!scene || zones.length === 0) return;
    if (queue.current.length === 0) queue.current = shuffle(zones);
    const next = queue.current.shift() ?? pick(zones);
    setDone(false);
    setPlacedAt(null);
    const id = await game.register({ scene: scene.id, mover: mover.word, place: next.id }, { value: next.id });
    if (!id) return;
    setTarget(next);
    setTrial(id);
  }, [scene?.id, wanted.join(), mover.word]);

  useEffect(() => { if (game.config) void start(); }, [game.config, start]);

  const prompt = target ? placePrompt(mover.word, target.label) : '';
  useEffect(() => {
    if (!target || game.textPrompt) return;
    const t = window.setTimeout(() => game.say(prompt), 300);
    return () => window.clearTimeout(t);
  }, [target, trial]);

  const placer = usePlacer((_chip, zone) => { void drop(zone); });

  async function drop(zone: string) {
    if (!target || !trial || done || busy.current) return;
    if (game.errorless && zone !== target.id) return;
    busy.current = true;
    const v = await game.respond(trial, zone);
    if (v?.correct) {
      setPlacedAt(zone);
      setDone(true);
      busy.current = false;
      return;
    }
    setPlacedAt(zone);
    window.setTimeout(() => setPlacedAt(null), 700);
    game.say(prompt);
    const fresh = await game.register({ scene: scene!.id, mover: mover.word, place: target.id, retry: true }, { value: target.id });
    busy.current = false;
    if (fresh) setTrial(fresh);
  }

  const fading = useRoundTimer(done, game.transitionMs, () => { void start(); });

  return (
    <GameFrame
      game={game}
      empty={!scene || zones.length === 0}
      emptyText={`${KINDS.placeIt.describes} Edit it to choose the places.`}
      onResume={() => { void start(); }}
      breakTrial={trial}
    >
      {scene && target && (
        <div className={`g-board g-place${fading ? ' is-fading' : ''}`}>
          <PromptLine game={game} text={prompt} />
          <SceneView
            scene={scene}
            placed={placedAt ? { [placedAt]: { emoji: mover.emoji, size: 46 } } : {}}
            overlay={(part) => {
              if (!wanted.includes(part.id) || placedAt) return null;
              const [x, y, w, h] = part.hit ?? part.box;
              const style = {
                left: `${(x / scene.w) * 100}%`,
                top: `${(y / scene.h) * 100}%`,
                width: `${(w / scene.w) * 100}%`,
                height: `${(h / scene.h) * 100}%`,
              };
              return (
                <DropSpot
                  key={part.id}
                  placer={placer}
                  id={part.id}
                  label={part.label}
                  className="is-zone"
                  style={style}
                  inert={game.errorless && part.id !== target.id}
                />
              );
            }}
          />
          <div className="g-tray" role="group" aria-label="Move this">
            {!placedAt && (
              <Chip placer={placer} id="mover" label={mover.word} className="is-mover" onTap={() => game.say(mover.word)}>
                <span className="g-chip-pic g-chip-emoji" aria-hidden="true">{mover.emoji}</span>
                <span className="g-chip-word">{mover.word}</span>
              </Chip>
            )}
          </div>
        </div>
      )}
    </GameFrame>
  );
}
